import React, { useRef, useEffect, useState } from "react";
import "./video-player.scss";
import { useEventRoomState } from "@/containers/root-container";
import useStream from "@/hooks/use-stream";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import RemoteAudioControl from "@/components/remote-audio-control/remote-audio-control";
import RemoteVideoControl from "@/components/remote-video-control/remote-video-control";
import pinIcon from "@/assets/pin-icon.png";
import unPinIcon from "@/assets/unpin-icon.png";
import { eventRoomStore } from "@/stores/event";
import * as Sentry from "@sentry/react";

interface VideoPlayerProps {
	domId?: string;
	id?: string;
	streamID: number;
	preview?: boolean;
	stream?: any;
	role?: string;
	audio: boolean;
	video: boolean;
	videoTurnedOn: boolean;
	className?: string;
	showProfile?: boolean;
	local?: boolean;
	handleClick?: (type: string, streamID: number, uid: string) => Promise<any>;
	close?: boolean;
	handleClose?: (uid: string, streamID: number) => void;
	autoplay?: boolean;
	onAutoPlayFailed?: (stream: any) => void;
	mainStream?: boolean;
	name: any;
	mainSt?: any;
	index?: any;
	handleSwitch?: (streamID: number) => void;
	liveStreams?: any;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
	role,
	stream,
	domId,
	streamID,
	audio,
	id,
	video,
	videoTurnedOn,
	local,
	name,
	handleSwitch,
	liveStreams = {},
}) => {
	const eventRoomState = useEventRoomState();
	const lockPlay = useRef<boolean>(false);
	const lockPlayAudio = useRef<boolean>(false);
	const { guideStream } = useStream(eventRoomStore);
	const videoFitPosition = useRef<string>("");
	useEffect(() => {
		const playStream = async () => {
			// Clear the previous details inside the main-video container
			const divElement = document.getElementById(`${domId}`);
			if (divElement) {
				divElement.innerHTML = "";
			}

			lockPlay.current = true;
			let isGuideStream = false;
			if (eventRoomState.me.info.role == "guide" && local) {
				isGuideStream = true;
			} else if (eventRoomState.me.info.role != "guide") {
				if (guideStream && guideStream?.streamID == streamID) {
					isGuideStream = true;
				}
			}

			const fit =
				isGuideStream && videoFitPosition.current
					? videoFitPosition.current
					: isGuideStream && !videoFitPosition.current
						? "contain"
						: "cover";

			if (
				typeof stream.videoTrack != "undefined" &&
				stream.videoTrack != null &&
				video &&
				videoTurnedOn
			) {
				if (local) {
					await stream.videoTrack.setEnabled(true); //Enable camera access when a user mutes their local video
				}
				// Defensive check: only play if the track is not already playing
				if (!stream.videoTrack.isPlaying) {
					console.log("[video-player] Playing video track for streamID:", streamID, "isPlaying:", stream.videoTrack.isPlaying);
					stream.videoTrack.play(`${streamID}`, { fit: fit }, (err: any) => {
						lockPlay.current = false;
						console.warn(
							"[video-player] ",
							JSON.stringify(err),
							id,
							stream.videoTrack.isPaused(),
							stream.videoTrack.isPlaying(),
							" isLocal: ",
							local
						);
					});
				} else {
					console.log("[video-player] Track already playing for streamID:", streamID);
					lockPlay.current = false;
				}

				if (isGuideStream) {
					const divElement = document.getElementById(`${streamID}`);
					const videoElement = divElement?.querySelector("video");

					videoElement?.addEventListener("resize", (e: any) => {
						const height = e.target?.videoHeight;
						const width = e.target?.videoWidth;
						if (height && width) {
							if (width > height) {
								videoFitPosition.current = "cover";
								videoElement.style.objectFit =
									videoFitPosition.current || "cover";
							} else {
								videoFitPosition.current = "contain";
								videoElement.style.objectFit =
									videoFitPosition.current || "contain";
							}
						}
					});
				}
			}
		};
		//if (!streamID || (stream.videoTrack==null && stream.audioTrack==null) || (stream.videoTrack!=null && stream.videoTrack.isPlaying) || (stream.audioTrack!=null && stream.audioTrack.isPlaying)) return;
		if (!streamID) return;
		if (video && videoTurnedOn) {
			playStream();
		} else {
			stopStream();
		}
		if (video && videoTurnedOn && stream?.videoTrack && !stream?.videoTrack?.isPlaying) {
			playStream();
		}
		return () => {
			if (stream.videoTrack && stream.videoTrack.isPlaying && stream) {
				try {
					if (local) {
						stream.videoTrack.setEnabled(false); //Disable camera access when a user mutes their local video
					}
					stream.videoTrack.stop();
					lockPlay.current = false;
				} catch (e) {
					console.warn("Stop streaming Error", e);
				}
			}
		};
	}, [streamID, video, videoTurnedOn, stream?.videoTrack]);

	useEffect(() => {
		if (!streamID) return;
		console.log("[audio-pipeline] effect run", {
			streamID,
			hasTrack: !!stream?.audioTrack,
			audio,
			local,
			isSelf: eventRoomState.me.uid === streamID,
		});
		Sentry.addBreadcrumb({
			category: "audio-pipeline",
			message: "effect run",
			level: "info",
			data: {
				streamID,
				hasTrack: !!stream?.audioTrack,
				audio,
				local,
				isSelf: eventRoomState.me.uid === streamID,
			},
		});
		playStreamAudio();

		return () => {
			const wasPlaying = !!(stream?.audioTrack && stream.audioTrack.isPlaying);
			console.log("[audio-pipeline] effect cleanup", {
				streamID,
				hasTrack: !!stream?.audioTrack,
				wasPlaying,
			});
			Sentry.addBreadcrumb({
				category: "audio-pipeline",
				message: "effect cleanup",
				level: "info",
				data: { streamID, hasTrack: !!stream?.audioTrack, wasPlaying },
			});
			if (stream.audioTrack && stream.audioTrack.isPlaying && stream) {
				try {
					stream.audioTrack.stop();
					console.log("[audio-pipeline] stop OK", { streamID });
					lockPlayAudio.current = false;
				} catch (e) {
					console.warn("[audio-pipeline] stop threw", { streamID, e });
					Sentry.captureException(e, {
						tags: { component: "video-player", op: "audio-stop" },
						extra: { streamID },
					});
				}
			}
		};
		// `audio` removed from deps on purpose. With setMuted (commit d9e1109),
		// Agora fires user-unpublished/user-published on every host mute/unmute
		// within ~50ms. Reacting to the `audio` flag here triggers a
		// stop()+play() cycle each toggle, and the cleanup stop() races the
		// immediate next play() — symptom: intermittent audio drops, plus
		// Sentry WS_ABORT/ERR_SUBSCRIBE_REQUEST_INVALID on slower clients
		// (Samsung Internet/Android observed). The audio element keeps playing
		// silence during a mute and auto-resumes when data flows — no replay
		// needed. We still react to `stream?.audioTrack` so real track swaps
		// (network blip → re-subscribe) still get a fresh play().
	}, [streamID, stream?.audioTrack]);

	const playStreamAudio = () => {
		lockPlayAudio.current = true;
		const hasTrack =
			typeof stream.audioTrack != "undefined" && stream.audioTrack != null;
		const isSelf = eventRoomState.me.uid === streamID;
		const willPlay = hasTrack && audio && !isSelf;
		const speakerId = sessionStorage.getItem("speakerId");

		console.log("[audio-pipeline] playStreamAudio", {
			streamID,
			willPlay,
			hasTrack,
			audio,
			isSelf,
			local,
			speakerId,
		});
		Sentry.addBreadcrumb({
			category: "audio-pipeline",
			message: willPlay ? "play attempt" : "play skipped",
			level: "info",
			data: { streamID, hasTrack, audio, isSelf, local, speakerId },
		});

		if (!willPlay) return;

		try {
			// Output-device selection is Chromium-only (Firefox/Safari throw
			// NOT_SUPPORTED). It used to sit unguarded at the top of this try:
			// the throw skipped play() entirely → NO AUDIO AT ALL on those
			// browsers whenever a speakerId was remembered (Sentry TM-FRONTEND-18).
			// Feature-detect + isolate so play() ALWAYS runs.
			if (
				!local &&
				speakerId &&
				typeof stream.audioTrack.setPlaybackDevice === "function"
			) {
				try {
					stream.audioTrack.setPlaybackDevice(speakerId);
				} catch (e) {
					console.warn("[audio-pipeline] setPlaybackDevice failed — playing on default output", { streamID, e });
				}
			}
			const playResult = stream.audioTrack.play();
			console.log("[audio-pipeline] play() called", { streamID });
			// In Agora 4.x, play() may return a Promise — surface rejections
			// (autoplay blocked, wrong output device, etc.) to Sentry.
			if (playResult && typeof playResult.then === "function") {
				playResult.catch((err: any) => {
					console.error("[audio-pipeline] play() rejected", { streamID, err });
					Sentry.captureException(err, {
						tags: { component: "video-player", op: "audio-play" },
						extra: { streamID, speakerId, local },
					});
				});
			}
			lockPlayAudio.current = true;
		} catch (e) {
			console.error("[audio-pipeline] play() threw", { streamID, e });
			Sentry.captureException(e, {
				tags: { component: "video-player", op: "audio-play" },
				extra: { streamID, speakerId, local },
			});
		}
	};

	const stopStream = async () => {
		// if (stream.videoTrack && stream.videoTrack.isPlaying) {
		try {
			stream?.videoTrack?.stop();
			lockPlay.current = false;
			const divElement = document.getElementById(`${streamID}`);
			const videoElement = divElement?.querySelector("video");
			videoElement?.remove();
		} catch (e) {
			console.warn("Stop streaming Error", e);
		}
		// }
	};

	const [screenshot, setScreenshot] = useState<any>(null);

	useEffect(() => {
		const takeScreenshot = async () => {
			if (
				video &&
				videoTurnedOn &&
				stream?.videoTrack &&
				stream?.videoTrack?.isPlaying
			) {
				const imageData = await stream.videoTrack.getCurrentFrameData();
				if (imageData) {
					const canvas = document.createElement("canvas");
					canvas.width = imageData.width;
					canvas.height = imageData.height;
					const context = canvas.getContext("2d");
					const imageDataObj = new ImageData(
						new Uint8ClampedArray(imageData.data),
						imageData.width,
						imageData.height
					);
					context?.putImageData(imageDataObj, 0, 0);
					const url = canvas.toDataURL();
					setScreenshot(url);
				}
			}
		};

		if (local || !streamID || !stream?.videoTrack?.isPlaying) return;

		// Set interval to take screenshot every 1 second
		const intervalId = setInterval(() => {
			takeScreenshot();
		}, 1000);

		// Clear the interval on component unmount or if dependencies change
		return () => clearInterval(intervalId);

		takeScreenshot();
	}, [local, streamID, stream?.videoTrack?.isPlaying]);

	return (
		<>
			<div
				id={`${domId}`}
				className={`agora-rtc-video ${local ? "rotateY180deg" : ""} ${eventRoomState.gridView ? "gridview" : ""
					}`}
			></div>
			{!local && screenshot !== null && videoTurnedOn && !video && (
				<div
					id="screenshot-video"
					style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0 }}
				>
					<img
						style={{
							width: "100%",
							height: "100%",
							objectFit: "cover",
						}}
						src={screenshot}
						alt="screenshot"
					/>
				</div>
			)}
			<div className="stream-details">
				<div className="streamUserName" title={name}>
					{name}
				</div>
			</div>
			{/* {!videoTurnedOn && stream?.videoTrack && (
				<div className="">
					<VideocamOffIcon className="videodisableicon" />
				</div>
			)}
			{!video && videoTurnedOn && local && (
				<div className="">
					<VideocamOffIcon className="videodisableicon" />
				</div>
			)}
			{!video && !videoTurnedOn && (
				<div className="">
					<VideocamOffIcon className="videodisableicon" />
				</div>
			)} */}
			{(!video || !videoTurnedOn || (local && !videoTurnedOn) && (!videoTurnedOn && stream?.videoTrack)) && (
				<div className="">
					<VideocamOffIcon className="videodisableicon" />
				</div>
			)}

			{(eventRoomState.me.info.role === "host" ||
				eventRoomState.me.info.role === "residence") &&
				streamID !== liveStreams.mainStreams[0]?.streamID && (
					<div
						className="stream-controls"
						style={{
							left: "50%",
							top: "50%",
							right: "auto",
							backgroundColor: "#FFFFFF",
							borderRadius: 100,
							width: 32,
							height: 32,
							transform: "translate(-50%, -50%)",
						}}
					>
						<button
							style={{
								backgroundColor: "transparent",
								border: 0,
								outline: "none",
								padding: 0,
								cursor: "pointer",
								width: 32,
								height: 32,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							onClick={() => {
								handleSwitch && handleSwitch(streamID);
							}}
						>
							<img src={pinIcon} alt="" style={{ width: "80%" }} />
						</button>
					</div>
				)}

			{(eventRoomState.me.info.role == "guide" ||
				eventRoomState.me.info.role == "host") &&
				!local &&
				!eventRoomStore.isProtectedTarget(streamID) && (
					<div className="stream-controls">
						<RemoteAudioControl streamID={streamID} audio={audio} />
					</div>
				)}
		</>
	);
};

export default React.memo(VideoPlayer);
